import React, { FC, useEffect, useMemo, useRef } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { NarvarEventTypes, useAnalyticsPost } from '../Analytics';
import { PageContext, usePageContext } from './usePageContext';
import useTitanAnalytics from './useTitanAnalytics';

export function growthbookAttributes({
  carrier,
  localeCountry,
  localeLanguage,
  orderNumbers,
  retailerMoniker,
  trackingNumbers,
  deviceType,
}: Pick<
  PageContext,
  | 'carrier'
  | 'localeCountry'
  | 'localeLanguage'
  | 'orderNumbers'
  | 'retailerMoniker'
  | 'trackingNumbers'
> & { deviceType?: 'mobile' | 'desktop' }) {
  const trackingNumber = trackingNumbers?.[0] ?? 'undefined';

  return {
    track_retailerMoniker: retailerMoniker,
    track_trackingNumber: trackingNumber,
    track_orderNumber: orderNumbers?.[0]
      ? `o-${retailerMoniker}-${orderNumbers[0]}`
      : `t-${carrier ?? 'undefined'}-${trackingNumber}`,
    track_localeCountry: localeCountry,
    track_localeLanguage: localeLanguage,
    track_deviceLayout: deviceType ?? '',
  };
}

const NarvarGrowthBookProvider: FC<{
  growthbookOptions: ConstructorParameters<typeof GrowthBook>[0];
  deviceType?: 'mobile' | 'desktop';
  children?: React.ReactNode;
}> = ({ growthbookOptions, deviceType, children }) => {
  // create a growthbook client when options are specified by the server
  const logEvent = useAnalyticsPost();
  const logEventRef = useRef(logEvent);
  logEventRef.current = logEvent;

  const titanAnalytics = useTitanAnalytics();
  const titanAnalyticsRef = useRef(titanAnalytics);

  // keep growthbook attributes in sync
  const { state: pageContext } = usePageContext();

  const gbAttributes = growthbookAttributes({ ...pageContext, deviceType });

  const growthbook = useMemo(
    () =>
      new GrowthBook({
        ...growthbookOptions,
        trackingCallback: (experiment, result) => {
          logEventRef.current({
            eventType: NarvarEventTypes.EXPERIMENT,
            config: [
              {
                name: 'experimentName',
                options: [experiment.key],
              },
              {
                name: 'choice',
                options: [result.value],
              },
            ],
          });

          titanAnalyticsRef.current
            ?.track({
              event: 'experiment_viewed',
              channel: 'web',
              type: 'track',
              context: {},
              properties: {
                experimentId: experiment.key,
                variationId: result.variationId.toString(),
                narvar_product_context: 'track',
                extra_properties: [
                  {
                    key: 'track_orderNumber',
                    value: gbAttributes?.track_orderNumber,
                  },
                  {
                    key: 'track_retailerMoniker',
                    value: gbAttributes?.track_retailerMoniker,
                  },
                  {
                    key: 'track_trackingNumber',
                    value: gbAttributes?.track_trackingNumber,
                  },
                  {
                    key: 'track_deviceLayout',
                    value: gbAttributes?.track_deviceLayout,
                  },
                  {
                    key: 'track_localeLanguage',
                    value: gbAttributes?.track_localeLanguage,
                  },
                  {
                    key: 'track_localeCountry',
                    value: gbAttributes?.track_localeCountry,
                  },
                ],
              },
            })
            // eslint-disable-next-line no-console
            .catch((e) => console.error(e));
        },
        onFeatureUsage: (featureKey, result) => {
          logEventRef.current({
            eventType: NarvarEventTypes.FEATURE_USAGE,
            config: [
              {
                name: 'featureName',
                options: [featureKey],
              },
              {
                name: 'value',
                options: [result.value],
              },
            ],
          });
        },
      }),
    [growthbookOptions, gbAttributes],
  );

  // configure growthbook to autorefresh features when attributes change
  useEffect(() => {
    growthbook?.loadFeatures({ autoRefresh: true }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('growthbook.loadFeatures threw', err);
    });
  }, [growthbook]);

  useEffect(() => {
    growthbook?.setAttributes(gbAttributes).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('growthbook.setAttributes threw', err);
    });
  }, [growthbook, gbAttributes]);

  return (
    <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
  );
};

export default NarvarGrowthBookProvider;
